import { defineNuxtPlugin, useRuntimeConfig } from "#app";

const SUPPORTED_VARIANTS = new Set(["local-dev", "staging"]);

/**
 * Normalizes the favicon variant value from the runtime config.
 * @param value The value to normalize.
 * @returns The normalized favicon variant, or an empty string if invalid.
 */
function normalizeVariant(value: unknown): string {
	if (typeof value !== "string") {
		return "";
	}

	const normalized = value.trim().toLowerCase();
	// Keep backward compatibility with the old localdev spelling.
	if (normalized === "localdev") {
		return "local-dev";
	}

	return SUPPORTED_VARIANTS.has(normalized) ? normalized : "";
}

/**
 * Rewrites the favicon href to include the specified variant folder.
 *
 * @param href The original favicon href.
 * @param variant The favicon variant to apply.
 * @returns The rewritten favicon href.
 */
function rewriteFaviconHref(href: string, variant: string): string {
	let url: URL;
	try {
		url = new URL(href, window.location.origin);
	} catch {
		// Skip invalid href values.
		return href;
	}

	// Only rewrite local assets, never external URLs.
	if (url.origin !== window.location.origin) {
		return href;
	}

	const fileName = url.pathname.split("/").filter(Boolean).pop();
	if (!fileName) {
		return href;
	}

	if (url.pathname.startsWith(`/${variant}/`)) {
		return `${url.pathname}${url.search}${url.hash}`;
	}

	// Keep query/hash intact and swap only the file path to the variant folder.
	url.pathname = `/${variant}/${fileName}`;
	return `${url.pathname}${url.search}${url.hash}`;
}

// This plugin rewrites the favicon hrefs to include a variant folder based on the runtime config.
export default defineNuxtPlugin((nuxtApp) => {
	const runtimeConfig = useRuntimeConfig();
	const variant = normalizeVariant(runtimeConfig.public.faviconVariant);

	if (!variant) {
		// No variant configured -> keep default favicon behavior.
		return;
	}

	const ensureLink = (rel: string, href: string): void => {
		if (document.head.querySelector(`link[rel='${rel}']`)) {
			return;
		}

		const link = document.createElement("link");
		link.setAttribute("rel", rel);
		link.setAttribute("href", href);

		if (rel.includes("icon")) {
			link.setAttribute("type", "image/x-icon");
		}

		document.head.appendChild(link);
	};

	const ensureFaviconLinks = (): void => {
		// Some pages do not declare favicon tags; create defaults so variant rewrite can still run.
		ensureLink("icon", "/favicon.ico?v3");
		ensureLink("apple-touch-icon", "/apple-touch-icon.png");
	};

	const applyVariantFavicons = (): void => {
		ensureFaviconLinks();

		const selectors = ["link[rel~='icon']", "link[rel='apple-touch-icon']"];

		document.querySelectorAll<HTMLLinkElement>(selectors.join(",")).forEach((link) => {
			const href = link.getAttribute("href");
			if (!href) {
				return;
			}

			const nextHref = rewriteFaviconHref(href, variant);
			if (nextHref !== href) {
				link.setAttribute("href", nextHref);
			}
		});
	};

	const scheduleApplyVariantFavicons = (): void => {
		requestAnimationFrame(applyVariantFavicons);
		window.setTimeout(applyVariantFavicons, 0);
		window.setTimeout(applyVariantFavicons, 100);
		window.setTimeout(applyVariantFavicons, 500);
	};

	const observer = new MutationObserver(() => {
		applyVariantFavicons();
	});

	// Initial run + rerun after route changes because page components define their own head tags.
	nuxtApp.hook("app:mounted", scheduleApplyVariantFavicons);
	nuxtApp.hook("app:mounted", () => {
		// Some pages update head tags after navigation, so keep favicon variants enforced.
		observer.observe(document.head, {
			childList: true,
			subtree: true,
			attributes: true,
			attributeFilter: ["href", "rel"],
		});
		window.addEventListener("load", applyVariantFavicons, { once: true });
	});
	nuxtApp.hook("page:finish", scheduleApplyVariantFavicons);
});
